import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { IconModule } from 'src/app/shared/icon/icon.module';
import {
  HealthStatus,
  HealthTarget,
  PlatformHealth,
  PlatformHealthService,
  UptimeRow,
} from 'src/app/core/services/platform-health.service';

const REFRESH_MS = 60_000;
const SEVERITY_ORDER: Record<HealthStatus, number> = { unknown: 0, ok: 1, degraded: 2, down: 3 };
/** How many unhealthy tenants the dashboard tile names before deferring to the page. */
const MAX_NAMED_TENANTS = 3;
const LOAD_ERROR = 'Could not load platform health';

const DOT_CLASS_BY_STATUS: Record<HealthStatus, string> = {
  ok: 'bg-emerald-500',
  degraded: 'bg-amber-500',
  down: 'bg-red-500',
  unknown: 'bg-gray-400',
};

@Component({
  selector: 'app-platform-health-card',
  standalone: true,
  imports: [CommonModule, RouterLink, IconModule],
  templateUrl: './platform-health-card.component.html',
})
export class PlatformHealthCardComponent implements OnInit, OnDestroy {
  private readonly platformHealthService = inject(PlatformHealthService);
  private refreshHandle?: ReturnType<typeof setInterval>;

  readonly health = signal<PlatformHealth | null>(null);
  readonly uptimeByKey = signal<Map<string, UptimeRow>>(new Map());
  readonly maxNamedTenants = MAX_NAMED_TENANTS;
  readonly checkupEnabled = signal(true);
  /** Set when the current-state call fails (401/500), so the card shows a line instead of staying blank. */
  readonly loadError = signal<string | null>(null);

  ngOnInit(): void {
    this.platformHealthService.getCheckupSettings().subscribe({
      next: (res) => {
        const enabled = res.data?.checkup_enabled ?? true;
        this.checkupEnabled.set(enabled);
        if (enabled) {
          this.load();
          this.startPolling();
        }
      },
      error: () => {
        this.checkupEnabled.set(true);
        this.load();
        this.startPolling();
      },
    });
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  private startPolling(): void {
    this.stopPolling();
    this.refreshHandle = setInterval(() => this.load(), REFRESH_MS);
  }

  private stopPolling(): void {
    if (this.refreshHandle != null) {
      clearInterval(this.refreshHandle);
      this.refreshHandle = undefined;
    }
  }

  private load(): void {
    this.platformHealthService.current().subscribe({
      next: (res) => {
        this.health.set(res.data);
        this.loadError.set(null);
      },
      error: () => this.loadError.set(LOAD_ERROR),
    });
    // A failed uptime call only leaves the percentages at "no data"; the rows still render.
    this.platformHealthService.uptime('24h').subscribe({
      next: (res) => {
        const rows = res.data?.rows ?? [];
        this.uptimeByKey.set(new Map(rows.map((row) => [`${row.target_kind}:${row.target_key}`, row])));
      },
      error: () => this.uptimeByKey.set(new Map()),
    });
  }

  /** Tenants that need attention, worst first — the only ones the dashboard names individually. */
  get unhealthyTenants(): HealthTarget[] {
    return (this.health()?.tenants ?? [])
      .filter((t) => t.status !== 'ok')
      .sort((a, b) => SEVERITY_ORDER[b.status] - SEVERITY_ORDER[a.status] || a.target_key.localeCompare(b.target_key));
  }

  get tenantTotal(): number {
    return this.health()?.tenants?.length ?? 0;
  }

  get tenantsOk(): number {
    return this.tenantTotal - this.unhealthyTenants.length;
  }

  /** Worst tenant status drives the summary tile's dot. */
  get monitoringPaused(): boolean {
    return !this.checkupEnabled();
  }

  get tenantSummaryStatus(): HealthStatus {
    const worst = this.unhealthyTenants[0];
    if (worst) return worst.status;
    return this.tenantTotal ? 'ok' : 'unknown';
  }

  failingChecks(target: HealthTarget): string[] {
    return Object.entries(target.checks)
      .filter(([, check]) => check.status === 'fail')
      .map(([name]) => name);
  }

  dotClass(status: HealthStatus): string {
    return DOT_CLASS_BY_STATUS[status];
  }

  uptimeText(kind: 'SERVICE' | 'TENANT', key: string): string {
    const row = this.uptimeByKey().get(`${kind}:${key}`);
    if (row?.uptime == null) return 'no data';
    return `${(row.uptime * 100).toFixed(1)}%`;
  }
}
